import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useReducedMotion } from "@/hooks/useReducedMotion";

const PAGES = [
  { label: "Home", path: "/" },
  { label: "Platform", path: "/platform" },
  { label: "Products", path: "/products" },
  { label: "About", path: "/about" },
  { label: "Insights", path: "/insights" },
  { label: "Contact", path: "/contact" },
];

// Witty responses to user input — all nudge toward going home
const RESPONSES: { match: RegExp; reply: string }[] = [
  { match: /^help$/i, reply: "Try clicking one of the links above. Even agents need directions sometimes." },
  { match: /^(ls|dir|ll)$/i, reply: "drwxr-xr-x  0 pages  0 bytes  (empty — you're in the void)" },
  { match: /^(cd|cd\s).*$/i, reply: "You can cd all you want. There's nothing here. Try / instead." },
  { match: /^(exit|quit|q)$/i, reply: "There's no exit from the void. But there is a homepage →  /" },
  { match: /^sudo\s/i, reply: "Nice try. Even root can't create pages that don't exist." },
  { match: /^(rm|delete|del)\s/i, reply: "You can't delete what doesn't exist. Philosophical, isn't it?" },
  { match: /^(cat|less|more|head|tail)\s/i, reply: "404: file not found. The cat has left the building." },
  { match: /^(ping|curl|wget)\s/i, reply: "Connection refused. This page is a dead end. Try cosx.co/ instead." },
  { match: /^(hello|hi|hey)/i, reply: "Hey! You seem lost. The homepage is warm and welcoming → /" },
  { match: /^(who|whoami)/i, reply: "You are: lost visitor #" + Math.floor(Math.random() * 9999) + ". Destination: unknown." },
  { match: /^(why|what)/i, reply: "Good question. The universe doesn't always have answers. But cosx.co/ does." },
  { match: /^(home|go home|go back|back)/i, reply: "Smart choice. Redirecting..." },
  { match: /^(fuck|shit|damn)/i, reply: "Frustration detected. Deploying comfort agent... just kidding. Try the homepage." },
  { match: /^clear$/i, reply: "You can clear the screen, but you can't clear the 404 from your heart." },
  { match: /^git\s/i, reply: "This isn't a repo. But if it were: git checkout main && go home." },
  { match: /^node|python|npm/i, reply: "Runtime not found in the void. All roads lead to / from here." },
  { match: /^make/i, reply: "make: *** No rule to make target 'this-page'. Stop. Try make home." },
];

const FALLBACK_RESPONSES = [
  "Command not recognized. The void echoes back silence... and a link to /",
  "Unknown command. Have you tried going home? It's nice there.",
  "Agent scratches head. That's not in the manual. Perhaps try one of the links above?",
  "Error: imagination.overflow. The agent suggests retreating to /",
  "Hmm, that's a new one. Still 404 though. The homepage awaits.",
  "The agent blinks. Twice. Then points firmly toward /",
];

type DisplayLine =
  | { type: "command"; text: string }
  | { type: "error"; text: string; color: "red" | "amber" }
  | { type: "info"; text: string }
  | { type: "links" }
  | { type: "redirect" }
  | { type: "user-cmd"; text: string }
  | { type: "response"; text: string }
  | { type: "mini-redirect"; id: number };

function buildInitialLines(path: string): DisplayLine[] {
  return [
    { type: "command", text: `$ curl cosx.co${path}` },
    { type: "error", text: "HTTP/1.1 404 Not Found", color: "red" },
    { type: "command", text: `$ cosx agent locate --path ${path}` },
    { type: "error", text: "Error: page not found in registry", color: "amber" },
    { type: "command", text: "$ cosx suggest" },
    { type: "info", text: "Available pages:" },
    { type: "links" },
    { type: "command", text: "$ cosx agent redirect --destination / --timeout 15" },
    { type: "redirect" },
  ];
}

const LINE_DELAY = 600;
const COUNTDOWN_START = 15;

export function TerminalScene() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const reduced = useReducedMotion();

  const initialLines = buildInitialLines(pathname);
  const [visibleCount, setVisibleCount] = useState(reduced ? initialLines.length : 0);
  const [countdown, setCountdown] = useState(COUNTDOWN_START);
  const [cancelled, setCancelled] = useState(false);
  const [extraLines, setExtraLines] = useState<DisplayLine[]>([]);
  const [userInput, setUserInput] = useState("");
  const [inputEnabled, setInputEnabled] = useState(false);
  const [miniCountdown, setMiniCountdown] = useState(-1);
  const miniIdRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const fallbackIdx = useRef(0);

  const advance = useCallback(() => {
    setVisibleCount((c) => (c < initialLines.length ? c + 1 : c));
  }, [initialLines.length]);

  // Typewriter
  useEffect(() => {
    if (reduced) { setVisibleCount(initialLines.length); return; }
    setVisibleCount(0);
    setCancelled(false);
    setCountdown(COUNTDOWN_START);
    setExtraLines([]);
    setInputEnabled(false);
    const first = setTimeout(() => setVisibleCount(1), 400);
    return () => clearTimeout(first);
  }, [pathname, reduced, initialLines.length]);

  useEffect(() => {
    if (reduced || visibleCount === 0 || visibleCount >= initialLines.length) return;
    const t = setTimeout(advance, LINE_DELAY);
    return () => clearTimeout(t);
  }, [visibleCount, initialLines.length, reduced, advance]);

  // Countdown
  const allVisible = visibleCount >= initialLines.length;
  useEffect(() => {
    if (!allVisible || cancelled) return;
    if (countdown <= 0) { navigate("/", { replace: true }); return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [allVisible, countdown, cancelled, navigate]);

  // Enable input after cancel
  useEffect(() => {
    if (cancelled) {
      setInputEnabled(true);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [cancelled]);

  // Mini countdown after each user command response
  useEffect(() => {
    if (miniCountdown <= 0) return;
    if (miniCountdown === 0) { navigate("/", { replace: true }); return; }
    const t = setTimeout(() => setMiniCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [miniCountdown, navigate]);

  // When mini countdown reaches 0, redirect
  useEffect(() => {
    if (miniCountdown === 0) navigate("/", { replace: true });
  }, [miniCountdown, navigate]);

  const cancelMini = useCallback(() => {
    setMiniCountdown(-1);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [extraLines, visibleCount]);

  const cancelRedirect = useCallback(() => {
    setCancelled(true);
    setCountdown(-1);
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const cmd = userInput.trim();
    if (!cmd) return;
    setUserInput("");

    // Check if "go home" type command
    const goHome = /^(home|go home|go back|back)$/i.test(cmd);

    // Find matching response
    let reply: string;
    const found = RESPONSES.find((r) => r.match.test(cmd));
    if (found) {
      reply = found.reply;
    } else {
      reply = FALLBACK_RESPONSES[fallbackIdx.current % FALLBACK_RESPONSES.length]!;
      fallbackIdx.current += 1;
    }

    miniIdRef.current += 1;
    const id = miniIdRef.current;

    setExtraLines((prev) => [
      ...prev,
      { type: "user-cmd", text: `$ ${cmd}` },
      { type: "response", text: reply },
      { type: "mini-redirect", id },
    ]);

    if (goHome) {
      setTimeout(() => navigate("/", { replace: true }), 1200);
    } else {
      setMiniCountdown(10);
    }
  }, [userInput, navigate]);

  const visible = initialLines.slice(0, visibleCount);
  const isTyping = visibleCount < initialLines.length;

  return (
    <div
      className="w-full max-w-2xl mx-auto rounded-lg border border-white/10 overflow-hidden"
      style={{ background: "#0a0a0a" }}
      onClick={() => inputEnabled && inputRef.current?.focus()}
    >
      {/* Title bar */}
      <div
        className="flex items-center gap-1.5 px-4 py-2 border-b border-white/10"
        style={{ background: "#141414" }}
      >
        <span className="w-3 h-3 rounded-full bg-red-500/80" />
        <span className="w-3 h-3 rounded-full bg-amber-500/80" />
        <span className="w-3 h-3 rounded-full bg-emerald-500/80" />
        <span className="ml-3 text-xs text-white/40 font-[family-name:var(--font-mono)]">
          cosx-terminal
        </span>
      </div>

      {/* Body */}
      <div ref={bodyRef} className="p-5 font-[family-name:var(--font-mono)] text-sm leading-relaxed min-h-[300px] max-h-[70vh] overflow-y-auto">
        {visible.map((line, i) => {
          const isLast = i === visible.length - 1;
          return (
            <div key={i} className="mb-1">
              {line.type === "command" && (
                <span className="text-emerald-500">
                  {line.text}
                  {isLast && isTyping && <Cursor />}
                </span>
              )}
              {line.type === "error" && (
                <span className={line.color === "red" ? "text-red-400" : "text-amber-400"}>
                  {line.text}
                </span>
              )}
              {line.type === "info" && (
                <span className="text-emerald-500">{line.text}</span>
              )}
              {line.type === "links" && (
                <div className="mt-1 ml-2 flex flex-col gap-0.5">
                  {PAGES.map((p) => (
                    <Link
                      key={p.path}
                      to={p.path}
                      onClick={cancelRedirect}
                      className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2 transition-colors w-fit"
                    >
                      → {p.label} <span className="text-white/30">{p.path}</span>
                    </Link>
                  ))}
                </div>
              )}
              {line.type === "redirect" && (
                <div className="text-white/50">
                  {cancelled ? (
                    <span className="text-amber-400">Redirect cancelled.</span>
                  ) : countdown > 0 ? (
                    <span>
                      Agent will bring you home in <span className="text-emerald-400 tabular-nums">{countdown}s</span>
                      <span className="text-white/30"> — </span>
                      <button
                        onClick={cancelRedirect}
                        className="text-amber-400 hover:text-amber-300 underline underline-offset-2 cursor-pointer"
                      >
                        cancel
                      </button>
                    </span>
                  ) : (
                    <span className="text-emerald-400">Redirecting...</span>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Extra lines from user interaction */}
        {extraLines.map((line, i) => (
          <div key={`extra-${i}`} className="mb-1">
            {line.type === "user-cmd" && (
              <span className="text-emerald-500">{line.text}</span>
            )}
            {line.type === "response" && (
              <span className="text-white/50">{line.text}</span>
            )}
            {line.type === "mini-redirect" && line.id === miniIdRef.current && miniCountdown > 0 && (
              <span className="text-white/40">
                Redirecting in <span className="text-emerald-400 tabular-nums">{miniCountdown}s</span>
                <span className="text-white/20"> — </span>
                <button onClick={cancelMini} className="text-amber-400 hover:text-amber-300 underline underline-offset-2 cursor-pointer">
                  cancel
                </button>
              </span>
            )}
            {line.type === "mini-redirect" && line.id === miniIdRef.current && miniCountdown === 0 && (
              <span className="text-emerald-400">Redirecting...</span>
            )}
            {line.type === "mini-redirect" && (line.id !== miniIdRef.current || miniCountdown < 0) && (
              <span className="text-amber-400">Redirect cancelled.</span>
            )}
          </div>
        ))}

        {/* Interactive input line — hidden during mini countdown */}
        {inputEnabled && miniCountdown <= 0 && (
          <form onSubmit={handleSubmit} className="flex items-center mt-1">
            <span className="text-emerald-500 shrink-0">$ </span>
            <span className="relative ml-1">
              {/* Invisible input for capturing keystrokes */}
              <input
                ref={inputRef}
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                className="absolute inset-0 w-full bg-transparent text-transparent outline-none border-none font-[family-name:var(--font-mono)] text-sm caret-transparent"
                autoComplete="off"
                spellCheck={false}
              />
              {/* Visible text + cursor immediately after */}
              <span className="text-emerald-500 whitespace-pre">{userInput}</span>
              <Cursor />
            </span>
          </form>
        )}
      </div>
    </div>
  );
}

function Cursor() {
  return (
    <span
      className="inline-block w-[7px] h-[1.1em] bg-emerald-500 ml-0.5 align-middle"
      style={{ animation: "cursorBlink 1s step-end infinite" }}
    />
  );
}
